import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Button,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const RejectOrderModal = ({
  open,
  onClose,
  onSubmit,
  t,
  isRTL,
  modificationDate,
}) => {
  const [selectedValue, setSelectedValue] = useState("");
  const [otherReason, setOtherReason] = useState("");

  const [remainingTime, setRemainingTime] = useState(null);
  const [isTimeExpired, setIsTimeExpired] = useState(false);

  const reasons = [
    { value: "Damaged shipment", label: t("damagedShipment") },
    { value: "Payment issues", label: t("paymentIssues") },
    { value: "Receiver refused to accept order", label: t("receiverRefused") },
    { value: "Other", label: t("other") },
  ];

  useEffect(() => {
    if (!open || !modificationDate) {
      setRemainingTime(null);
      setIsTimeExpired(false);
      return;
    }

    const modificationTime = new Date(modificationDate);
    const expirationTime = new Date(modificationTime.getTime() + 5 * 60 * 1000);

    const initialSecondsLeft = Math.round(
      (expirationTime.getTime() - new Date().getTime()) / 1000
    );
    if (initialSecondsLeft <= 0) {
      setIsTimeExpired(true);
      setRemainingTime(0);
      return;
    }

    const intervalId = setInterval(() => {
      const now = new Date();
      const secondsLeft = Math.round(
        (expirationTime.getTime() - now.getTime()) / 1000
      );

      if (secondsLeft <= 0) {
        clearInterval(intervalId);
        setRemainingTime(0);
        setIsTimeExpired(true);
      } else {
        setRemainingTime(secondsLeft);
        setIsTimeExpired(false);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [open, modificationDate]);

  useEffect(() => {
    if (!open) {
      setSelectedValue("");
      setOtherReason("");
    }
  }, [open]);

  const handleRadioChange = (event) => {
    setSelectedValue(event.target.value);
  };

  const handleOtherReasonChange = (event) => {
    setOtherReason(event.target.value);
  };

  const handleSave = () => {
    const finalReason = selectedValue === "Other" ? otherReason : selectedValue;
    onSubmit(finalReason);
  };

  const formatTime = (totalSeconds) => {
    if (totalSeconds === null || totalSeconds < 0) return "";
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  const isSaveDisabled =
    !selectedValue || (selectedValue === "Other" && !otherReason.trim());

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "24px",
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        {t("rejectedOrder")}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: isRTL ? "auto" : 8,
            left: isRTL ? 8 : "auto",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <FormControl component="fieldset" sx={{ width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              gap: "6px",
              mb: 1,
            }}
          >
            <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>
              {t("reasonForOrderUpdate")}
            </Typography>
            <Typography>{t("pleaseSelectReason")}</Typography>
          </Box>

          <RadioGroup value={selectedValue} onChange={handleRadioChange}>
            {reasons.map((reason) => (
              <Box key={reason.value}>
                <FormControlLabel
                  value={reason.value}
                  control={
                    <Radio
                      sx={{
                        "&.Mui-checked": {
                          color: "#2A3467",
                        },
                      }}
                    />
                  }
                  label={reason.label}
                />
                {reason.value === "Other" && selectedValue === "Other" && (
                  <TextField
                    autoFocus
                    margin="dense"
                    placeholder={t("typeReason")}
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={otherReason}
                    onChange={handleOtherReasonChange}
                    sx={{
                      ml: 4,
                      width: "calc(100% - 32px)",
                      "& input": {
                        fontSize: "14px",
                        fontWeight: 400,
                        mb: 8,
                      },
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: "#2A3467",
                        },
                      },
                    }}
                  />
                )}
              </Box>
            ))}
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ p: "12px 0" }}>
        <Button
          onClick={handleSave}
          variant="outlined"
          disabled={isSaveDisabled || !isTimeExpired}
          sx={{
            color: "#fff",
            borderColor: "#2A3467",
            padding: "8px 35px",
            minWidth: "100px",
            backgroundColor: "#2A3467",
            "&:hover": {
              backgroundColor: "#fff",
              color: "#2A3467",
              borderColor: "#2A3467",
            },
            "&:disabled": {
              backgroundColor: "#cccccc",
              color: "#888888",
              borderColor: "#cccccc",
            },
          }}
        >
          {!isTimeExpired && remainingTime > 0
            ? formatTime(remainingTime)
            : t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RejectOrderModal;
